import { Cookie, Droplets, Egg, Hourglass, Scissors, UtensilsCrossed } from "lucide-react";
import Reveal from "@/components/Reveal";

const TIPS = [
  {
    icon: Egg,
    title: "Room-temperature eggs",
    text: "Cold eggs seize the batter and dull the rise. Set them on the counter 20 minutes before you start — the crumb comes out noticeably lighter.",
  },
  {
    icon: UtensilsCrossed,
    title: "Whisk for 60 seconds, max",
    text: "Organic almond flour doesn't need beating. Mix until the last streak disappears and stop — overworking makes the cake dense instead of tender.",
  },
  {
    icon: Hourglass,
    title: "Let the batter rest 5 minutes",
    text: "A short rest lets the organic almond flour drink up the moisture. The batter thickens slightly and bakes up more even, edge to center.",
  },
  {
    icon: Scissors,
    title: "Line the pan, don't just grease",
    text: "Almond cakes are delicate while warm. A parchment sling means the whole cake lifts out in one proud, unbroken piece.",
  },
  {
    icon: Droplets,
    title: "Trust moist crumbs, not a dry skewer",
    text: "Pull the cake when the skewer shows a few moist crumbs. Bone-dry means overbaked — carryover heat finishes the job on the counter.",
  },
  {
    icon: Cookie,
    title: "Serve it warm",
    text: "Don't wait for it to cool — an organic almond flour cake is at its softest, most fragrant best straight from the oven. Gather everyone and slice it warm.",
  },
];

export default function Tips() {
  return (
    <section id="tips" className="bg-[#F3EDE2] py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="mb-14 sm:mb-16 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#C86D51] mb-4">Baking Tips</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#2C221E]">
              Little tricks for a<br /><em className="italic text-[#2A524A]">perfect</em> crumb
            </h2>
          </div>
          <p className="text-[#6E5E57] max-w-sm text-sm sm:text-base leading-relaxed">
            Organic almond flour plays by its own rules — gentler, richer, more forgiving than you'd
            think. Six habits our test kitchen swears by.
          </p>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tips-grid">
          {TIPS.map((tip, i) => (
            <Reveal key={tip.title} delay={(i % 3) * 0.08}>
              <div
                data-testid={`tip-card-${i}`}
                className="h-full rounded-3xl bg-[#FAF7F2] border border-[#2C221E]/10 p-7 sm:p-8 hover:border-[#2C221E]/25 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-[#2C221E]/10 transition-all duration-300 group"
              >
                <div className="flex items-center justify-between">
                  <span className="w-11 h-11 rounded-2xl bg-[#D1E8E2] text-[#2A524A] flex items-center justify-center group-hover:bg-[#F8C8DC] group-hover:text-[#6A2C3E] group-hover:-rotate-6 transition-all duration-300">
                    <tip.icon className="w-5 h-5" />
                  </span>
                  <span className="font-mono text-xs tracking-[0.25em] text-[#C86D51]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-serif text-xl sm:text-2xl font-semibold text-[#2C221E] mt-6 leading-snug">
                  {tip.title}
                </h3>
                <p className="mt-3 text-sm text-[#6E5E57] leading-relaxed">{tip.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
